import { useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import toast from 'react-hot-toast';

interface WebSocketMessage {
  type: 'incident' | 'threat' | 'alert' | 'notification' | 'status_update';
  id: string;
  title: string;
  message: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  timestamp: string;
  data?: Record<string, unknown>;
}

interface AuthenticatedData {
  userId: string;
  message: string;
}

interface AuthError {
  message: string;
  code?: string;
}

interface ConnectedData {
  message: string;
  timestamp?: string;
}

interface UseWebSocketOptions {
  url?: string;
  autoConnect?: boolean;
  reconnectAttempts?: number;
  reconnectDelay?: number;
  showToastNotifications?: boolean;
}

export function useWebSocket({
  url = import.meta.env.VITE_WS_URL ?? '',
  autoConnect = true,
  reconnectAttempts = 5,
  reconnectDelay = 3000,
  showToastNotifications = true,
}: UseWebSocketOptions = {}) {
  const socketRef = useRef<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionState, setConnectionState] = useState<'connecting' | 'connected' | 'disconnected' | 'error'>('disconnected');
  const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null);
  const [messages, setMessages] = useState<WebSocketMessage[]>([]);

  const connect = () => {
    if (socketRef.current?.connected) {
      return;
    }

    setConnectionState('connecting');

    try {
      // Create Socket.IO client
      socketRef.current = io(url, {
        reconnection: true,
        reconnectionAttempts: reconnectAttempts,
        reconnectionDelay: reconnectDelay,
        reconnectionDelayMax: reconnectDelay * 5,
        timeout: 20000, // Increased from 10s to 20s to match server ping settings
        transports: ['websocket', 'polling'],
        autoConnect: false,
      });

      // Connection event handlers
      socketRef.current.on('connect', () => {
        setIsConnected(true);
        setConnectionState('connected');

        // Send authentication token if available
        const token = localStorage.getItem('access_token');
        if (token && socketRef.current) {
          socketRef.current.emit('auth', { token });
        }
      });

      // Handle successful authentication
      socketRef.current.on('authenticated', (_data: AuthenticatedData) => {
        // Authentication successful
      });

      // Handle authentication errors
      socketRef.current.on('auth_error', (error: AuthError) => {
        console.error('[Socket.IO] Authentication failed:', error);
        setConnectionState('error');
      });

      socketRef.current.on('disconnect', (_reason) => {
        setIsConnected(false);
        setConnectionState('disconnected');
      });

      socketRef.current.on('connect_error', (error) => {
        setConnectionState('error');
        console.error('[Socket.IO] Connection error:', error.message);
      });

      socketRef.current.on('error', (error) => {
        setConnectionState('error');
        console.error('[Socket.IO] Error:', error);
      });

      // Listen for notification events
      socketRef.current.on('notification', (data: WebSocketMessage) => {
        setLastMessage(data);
        setMessages(prev => [data, ...prev.slice(0, 99)]); // Keep last 100 messages

        // Show toast notification for important messages
        if (showToastNotifications && ['incident', 'threat', 'alert', 'notification'].includes(data.type)) {
          const toastOptions = {
            duration: data.severity === 'critical' || data.severity === 'high' ? 8000 : 4000,
          };

          switch (data.severity) {
            case 'critical':
              toast.error(`🚨 ${data.title}: ${data.message}`, toastOptions);
              break;
            case 'high':
              toast.error(`⚠️ ${data.title}: ${data.message}`, toastOptions);
              break;
            case 'medium':
              toast(`⚡ ${data.title}: ${data.message}`, toastOptions);
              break;
            case 'low':
              toast.success(`ℹ️ ${data.title}: ${data.message}`, toastOptions);
              break;
          }
        }
      });

      // Listen for welcome message
      socketRef.current.on('connected', (_data: ConnectedData) => {
        // Connected to server
      });

      // Connect the socket
      socketRef.current.connect();
    } catch (error) {
      setConnectionState('error');
      console.error('[Socket.IO] Failed to create connection:', error);
    }
  };

  const disconnect = () => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current.removeAllListeners();
      socketRef.current = null;
    }

    setIsConnected(false);
    setConnectionState('disconnected');
  };

  const send = (event: string, data: unknown) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit(event, data);
      return true;
    }
    return false;
  };

  const clearMessages = () => {
    setMessages([]);
    setLastMessage(null);
  };

  // Auto-connect on mount
  useEffect(() => {
    if (autoConnect) {
      connect();
    }

    // Cleanup on unmount
    return () => {
      disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- connect/disconnect are stable functions, only react to url/autoConnect changes
  }, [url, autoConnect]);

  return {
    isConnected,
    connectionState,
    lastMessage,
    messages,
    connect,
    disconnect,
    send,
    clearMessages,
    socket: socketRef.current,
  };
}
